import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import axios from 'axios';
import { toast } from 'react-toastify';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const DEFAULT_PAYMENT_BYPASS_EMAIL = 'test-bypass@visioning-assessment.com';

const getPaymentBypassEmails = () => {
  const configured = process.env.REACT_APP_TEST_PAYMENT_BYPASS_EMAILS ||
    process.env.REACT_APP_TEST_PAYMENT_BYPASS_EMAIL ||
    DEFAULT_PAYMENT_BYPASS_EMAIL;

  return configured
    .split(',')
    .map((email) => email.trim().toLowerCase())
    .filter(Boolean);
};

const isPaymentBypassTester = (practitioner) => {
  if (!practitioner) return false;

  const paymentDetails = practitioner.paymentDetails || {};
  const email = (practitioner.email || '').trim().toLowerCase();

  return Boolean(
    paymentDetails.testAccount ||
    paymentDetails.paymentBypass ||
    practitioner.licenseNumber === 'TEST-BYPASS-ACCOUNT' ||
    getPaymentBypassEmails().includes(email)
  );
};

// ─── Animations ───────────────────────────────────────────────────────────────

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0%   { background-position: -400px 0; }
  100% { background-position:  400px 0; }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

// ─── Styled Components ────────────────────────────────────────────────────────

const Container = styled.div`
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;

  h1 {
    font-size: 1.6rem;
    font-weight: 800;
    color: #0f172a;
    letter-spacing: -0.02em;
  }
`;

const FiltersBar = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const Select = styled.select`
  padding: 10px 16px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  font-size: 0.9rem;
  background: white;
  color: #334155;
  outline: none;
  cursor: pointer;
  transition: border-color 0.2s;

  &:focus { border-color: #6366f1; }
`;

const Input = styled.input`
  padding: 10px 16px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  font-size: 0.9rem;
  min-width: 260px;
  color: #334155;
  outline: none;
  transition: border-color 0.2s;

  &:focus { border-color: #6366f1; }
`;

const PractitionersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(270px, 1fr));
  gap: 20px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const PractitionerCard = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.07);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: transform 0.25s, box-shadow 0.25s;
  animation: ${fadeIn} 0.3s ease both;

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.12);
  }
`;

const PractitionerImageWrapper = styled.div`
  width: 100%;
  height: 190px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  overflow: hidden;
  position: relative;
`;

const PractitionerImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const NoImagePlaceholderCard = styled.div`
  position: absolute;
  inset: 0;
  font-size: 3rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.8);
`;

const StatusBadgeCard = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
`;

const PractitionerInfo = styled.div`
  padding: 18px;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const PractitionerName = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PractitionerEmail = styled.p`
  font-size: 0.82rem;
  color: #64748b;
  margin: 0 0 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PractitionerLicense = styled.p`
  font-size: 0.78rem;
  color: #94a3b8;
  margin: 0 0 12px;
  font-weight: 500;
`;

const PractitionerActions = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: auto;
  padding-top: 12px;
  border-top: 1px solid #f1f5f9;
`;

const Badge = styled.span`
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: ${({ type }) =>
    ({ pending: '#fef3c7', approved: '#dcfce7', rejected: '#fee2e2' }[type] ||
    '#f1f5f9')};
  color: ${({ type }) =>
    ({ pending: '#d97706', approved: '#16a34a', rejected: '#dc2626' }[type] ||
    '#64748b')};
`;

const ActionButton = styled.button`
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  flex: 1;
  border: none;
  transition: all 0.18s;

  ${({ approve }) =>
    approve &&
    `background: #dcfce7; color: #16a34a;
     &:hover { background: #bbf7d0; }`}

  ${({ reject }) =>
    reject &&
    `background: #fee2e2; color: #dc2626;
     &:hover { background: #fecaca; }`}

  ${({ view }) =>
    view &&
    `background: #ede9fe; color: #7c3aed;
     &:hover { background: #ddd6fe; }`}
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 1000;
  backdrop-filter: blur(4px);
  animation: ${fadeIn} 0.2s ease;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 20px;
  max-width: 1000px;
  width: 100%;
  max-height: 92vh;
  overflow: hidden;
  box-shadow: 0 32px 80px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  animation: ${fadeIn} 0.25s ease;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 28px;
  border-bottom: 1px solid #f1f5f9;
  flex-shrink: 0;

  h2 {
    font-size: 1.15rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .icon-btn {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 1rem;
    transition: all 0.18s;

    &:hover { background: #f1f5f9; }
  }
`;

const ModalBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px 28px;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 24px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const InfoItem = styled.div`
  padding: 12px 14px;
  background: #f8fafc;
  border-radius: 10px;
  border: 1px solid #f1f5f9;

  label {
    display: block;
    font-size: 0.7rem;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-bottom: 3px;
    font-weight: 600;
  }

  span {
    font-weight: 600;
    color: #1e293b;
    font-size: 0.9rem;
  }
`;

const SpecList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;

  span {
    background: #ede9fe;
    color: #6d28d9;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 0.78rem;
    font-weight: 600;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #f1f5f9;
  flex-wrap: wrap;
`;

const Button = styled.button`
  flex: 1;
  min-width: 100px;
  padding: 11px 16px;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;
  border: none;
  transition: all 0.18s;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
    filter: grayscale(0.2);
  }

  ${({ primary }) =>
    primary &&
    `background: #16a34a; color: white;
     &:hover { background: #15803d; }`}

  ${({ danger }) =>
    danger &&
    `background: #dc2626; color: white;
     &:hover { background: #b91c1c; }`}

  ${({ secondary }) =>
    secondary &&
    `background: #f1f5f9; color: #475569;
     border: 1px solid #e2e8f0;
     &:hover { background: #e2e8f0; }`}
`;

const ProtectedAccountNotice = styled.div`
  margin-top: 18px;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid #fed7aa;
  background: #fff7ed;
  color: #9a3412;
  font-size: 0.88rem;
  line-height: 1.5;
  font-weight: 600;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  min-height: 110px;
  margin-top: 14px;
  font-size: 0.9rem;
  color: #334155;
  outline: none;
  resize: vertical;
  transition: border-color 0.2s;
  box-sizing: border-box;

  &:focus { border-color: #6366f1; }
`;

const MediaGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 20px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const MediaSection = styled.div`
  background: #f8fafc;
  border-radius: 14px;
  padding: 16px;
  border: 1px solid #e2e8f0;
`;

const MediaLabel = styled.p`
  font-size: 0.7rem;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 700;
  margin: 0 0 10px;
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 260px;
  object-fit: contain;
  border-radius: 10px;
  background: white;
  border: 1px solid #e2e8f0;
  cursor: zoom-in;
  transition: transform 0.2s;

  &:hover { transform: scale(1.02); }
`;

const EmptyMedia = styled.div`
  width: 100%;
  height: 260px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: white;
  border-radius: 10px;
  border: 2px dashed #e2e8f0;
  color: #cbd5e1;
  gap: 8px;

  .icon  { font-size: 2.8rem; }
  .label { font-size: 0.82rem; font-weight: 500; }
`;

const DocCard = styled.div`
  width: 100%;
  min-height: 260px;
  border-radius: 10px;
  border: 1px solid #dbeafe;
  background: linear-gradient(145deg, #eff6ff 0%, #eef2ff 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 20px;
  text-align: center;
  box-sizing: border-box;

  .doc-icon     { font-size: 3.5rem; }
  .doc-title    { font-weight: 700; font-size: 0.95rem; color: #1e3a8a; }
  .doc-subtitle { font-size: 0.8rem; color: #3b82f6; }
`;

const DocActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
  flex-wrap: wrap;
  justify-content: center;
`;

const DocBtn = styled.button`
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.18s;

  ${({ primary }) =>
    primary &&
    `background: #2563eb; color: white;
     &:hover { background: #1d4ed8; }`}

  ${({ secondary }) =>
    secondary &&
    `background: #dbeafe; color: #1d4ed8;
     &:hover { background: #bfdbfe; }`}
`;

const LightboxOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.96);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${fadeIn} 0.2s ease;
`;

const LightboxClose = styled.button`
  position: absolute;
  top: 18px;
  right: 18px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  border-radius: 50%;
  width: 44px;
  height: 44px;
  font-size: 1.4rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.18s;

  &:hover { background: rgba(255, 255, 255, 0.22); }
`;

const LightboxImage = styled.img`
  max-width: 92vw;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 6px;
  box-shadow: 0 0 60px rgba(0, 0, 0, 0.5);
`;

const PDFViewerOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.92);
  z-index: 2000;
  display: flex;
  flex-direction: column;
  animation: ${fadeIn} 0.2s ease;
`;

const PDFToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  background: #1e293b;
  flex-shrink: 0;
  gap: 12px;

  .title {
    color: white;
    font-weight: 600;
    font-size: 0.95rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .toolbar-actions {
    display: flex;
    gap: 8px;
    flex-shrink: 0;
  }
`;

const ToolbarBtn = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  border-radius: 8px;
  padding: 6px 14px;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background 0.18s;

  &:hover { background: rgba(255, 255, 255, 0.2); }
`;

const PDFFrame = styled.div`
  flex: 1;
  overflow: hidden;
  position: relative;

  iframe {
    width: 100%;
    height: 100%;
    border: none;
    display: block;
  }
`;

const SpinnerWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 16px;
  color: #94a3b8;
  font-size: 0.88rem;
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid #e2e8f0;
  border-top-color: #6366f1;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;


const PaginationBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 24px;
  flex-wrap: wrap;
`;

const PageButton = styled.button`
  border: 1px solid #e2e8f0;
  background: white;
  color: #334155;
  border-radius: 10px;
  padding: 9px 14px;
  font-size: 0.84rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.18s;

  &:hover:not(:disabled) {
    background: #eef2ff;
    border-color: #c7d2fe;
    color: #4f46e5;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const PageStatus = styled.span`
  color: #64748b;
  font-size: 0.86rem;
  font-weight: 700;
`;

const SkeletonCard = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.07);
  overflow: hidden;
  height: 320px;

  .shimmer {
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      #f1f5f9 25%,
      #e2e8f0 50%,
      #f1f5f9 75%
    );
    background-size: 800px 100%;
    animation: ${shimmer} 1.4s infinite;
  }
`;

// ─── Utility helpers ──────────────────────────────────────────────────────────

const hasFileReference = (ref) => {
  if (ref === null || ref === undefined || ref === '') return false;
  if (typeof ref === 'number') return ref > 0;
  if (typeof ref === 'string') return ref.trim() !== '';
  return false;
};

const isPdfReference = (ref) => {
  if (!hasFileReference(ref)) return false;
  if (typeof ref === 'string' && ref.toLowerCase().endsWith('.pdf')) return true;
  return false;
};

// ─────────────────────────────────────────────────────────────────────────────

const Practitioners = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [practitioners, setPractitioners]               = useState([]);
  const [loading, setLoading]                           = useState(true);
  const [selectedPractitioner, setSelectedPractitioner] = useState(null);
  const [showRejectModal, setShowRejectModal]           = useState(false);
  const [rejectReason, setRejectReason]                 = useState('');
  const [page, setPage]                                 = useState(1);
  const [totalPages, setTotalPages]                     = useState(1);
  const [totalPractitioners, setTotalPractitioners]       = useState(0);
  const [lightbox, setLightbox]                         = useState(null);
  const [pdfViewer, setPdfViewer]                       = useState(null);
  const [fileMimeTypes, setFileMimeTypes]               = useState({});
  const [blobCache, setBlobCache]                       = useState({});
  const [loadingFile, setLoadingFile]                   = useState(false);

  const statusFilter = searchParams.get('status') || '';
  const searchQuery  = searchParams.get('search')  || '';

  useEffect(() => {
    setPage(1);
  }, [statusFilter, searchQuery]);

  // ── Token helper ──────────────────────────────────────────────────────────
  const getToken = useCallback(
    () =>
      localStorage.getItem('admin_token') ||
      localStorage.getItem('token') ||
      axios.defaults.headers.common.Authorization
        ?.replace(/^Bearer\s+/i, '')
        .trim() ||
      '',
    []
  );

  // ── Authenticated URL builder ─────────────────────────────────────────────
  const getSecureFileUrl = useCallback(
    (fileRef) => {
      if (!fileRef) return '';
      const token = getToken();

      if (typeof fileRef === 'string' && fileRef.includes('/')) {
        const norm = fileRef.replace(/^\/+/, '');
        const base = `${API_URL}/files/download/${norm}`;
        return token ? `${base}?token=${encodeURIComponent(token)}` : base;
      }

      const base = `${API_URL}/files/download/${fileRef}`;
      return token ? `${base}?token=${encodeURIComponent(token)}` : base;
    },
    [getToken]
  );

  // ── Fetch file as Blob → blob:// URL (bypasses X-Frame-Options) ───────────
  const fetchBlob = useCallback(
    async (fileRef) => {
      if (blobCache[fileRef]) return blobCache[fileRef];

      const url   = getSecureFileUrl(fileRef);
      const token = getToken();

      const response = await axios.get(url, {
        responseType: 'blob',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      const blobUrl = URL.createObjectURL(response.data);
      setBlobCache((prev) => ({ ...prev, [fileRef]: blobUrl }));
      return blobUrl;
    },
    [blobCache, getSecureFileUrl, getToken]
  );

  // ── MIME detection ────────────────────────────────────────────────────────
  const detectMime = useCallback(
    async (fileRef) => {
      if (!hasFileReference(fileRef)) return null;
      if (fileMimeTypes[fileRef]) return fileMimeTypes[fileRef];

      try {
        const url = getSecureFileUrl(fileRef);
        const res = await axios.head(url);
        const mime = res.headers?.['content-type'] || null;
        if (mime) setFileMimeTypes((prev) => ({ ...prev, [fileRef]: mime }));
        return mime;
      } catch {
        return null;
      }
    },
    [fileMimeTypes, getSecureFileUrl]
  );

  const isMimePdf = (fileRef) => {
    if (isPdfReference(fileRef)) return true;
    const m = fileMimeTypes[fileRef];
    return typeof m === 'string' && m.toLowerCase().includes('application/pdf');
  };

  const getLicenseTypeDisplay = useCallback((practitioner) => {
    if (!practitioner) return 'N/A';
    const licenseType = String(practitioner.licenseType || '').trim();
    const licenseTypeOther = String(
      practitioner.licenseTypeOther ||
      practitioner.customLicenseType ||
      practitioner.licenseTypeCustom ||
      ''
    ).trim();

    if (licenseType.toLowerCase() === 'other' && licenseTypeOther) {
      return licenseTypeOther;
    }

    return licenseType || 'N/A';
  }, []);

  // ── Open image lightbox ───────────────────────────────────────────────────
  const openImage = useCallback(
    async (fileRef, title) => {
      if (!hasFileReference(fileRef)) return;
      setLoadingFile(true);
      try {
        const blobUrl = await fetchBlob(fileRef);
        setLightbox({ src: blobUrl, title });
      } catch {
        toast.error('Could not load image.');
      } finally {
        setLoadingFile(false);
      }
    },
    [fetchBlob]
  );

  // ── Open PDF viewer (blob iframe → no X-Frame-Options issue) ─────────────
  const openPDF = useCallback(
    async (fileRef, title) => {
      if (!hasFileReference(fileRef)) return;
      setLoadingFile(true);
      try {
        const blobUrl = await fetchBlob(fileRef);
        setPdfViewer({ blobUrl, title, fileRef });
      } catch {
        toast.error('Could not load document.');
      } finally {
        setLoadingFile(false);
      }
    },
    [fetchBlob]
  );

  // ── Smart open: detect type then route ────────────────────────────────────
  const openFile = useCallback(
    async (fileRef, title) => {
      if (!hasFileReference(fileRef)) return;
      setLoadingFile(true);
      try {
        let mime = fileMimeTypes[fileRef];
        if (!mime) mime = await detectMime(fileRef);

        const isPDF =
          isPdfReference(fileRef) ||
          (typeof mime === 'string' &&
            mime.toLowerCase().includes('application/pdf'));

        if (isPDF) {
          await openPDF(fileRef, title);
        } else {
          await openImage(fileRef, title);
        }
      } catch {
        toast.error('Could not open file.');
      } finally {
        setLoadingFile(false);
      }
    },
    [fileMimeTypes, detectMime, openPDF, openImage]
  );

  // ── Download via blob ─────────────────────────────────────────────────────
  const downloadFile = useCallback(
    async (fileRef, filename = 'document') => {
      try {
        const blobUrl = await fetchBlob(fileRef);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename;
        a.click();
      } catch {
        toast.error('Download failed.');
      }
    },
    [fetchBlob]
  );

  // ── Cleanup blob URLs on unmount ──────────────────────────────────────────
  useEffect(() => {
    return () => {
      Object.values(blobCache).forEach((url) => URL.revokeObjectURL(url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Data loading ──────────────────────────────────────────────────────────
  useEffect(() => {
    loadPractitioners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, searchQuery, page]);

  useEffect(() => {
    if (selectedPractitioner?.licenseDocumentPath) {
      detectMime(selectedPractitioner.licenseDocumentPath);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPractitioner?.licenseDocumentPath]);

  const loadPractitioners = async () => {
    try {
      setLoading(true);
      const params = { page, limit: 20 };
      if (statusFilter) params.status = statusFilter;
      if (searchQuery.trim()) params.search = searchQuery.trim();

      const response = await axios.get(`${API_URL}/admin/practitioners`, {
        params,
      });
      setPractitioners(response.data.practitioners || []);
      setTotalPages(response.data.totalPages || 1);
      setTotalPractitioners(response.data.total || 0);
    } catch {
      toast.error('Failed to load practitioners');
    } finally {
      setLoading(false);
    }
  };

  // ── Action handlers ───────────────────────────────────────────────────────
  const handleApprove = async (id) => {
    try {
      await axios.post(`${API_URL}/admin/practitioners/${id}/approve`);
      toast.success('Practitioner approved!');
      loadPractitioners();
      setSelectedPractitioner(null);
    } catch {
      toast.error('Failed to approve practitioner');
    }
  };

  const handleReject = async () => {
    try {
      await axios.post(
        `${API_URL}/admin/practitioners/${selectedPractitioner.id}/reject`,
        { reason: rejectReason }
      );
      toast.success('Practitioner rejected');
      setShowRejectModal(false);
      setRejectReason('');
      setSelectedPractitioner(null);
      loadPractitioners();
    } catch {
      toast.error('Failed to reject practitioner');
    }
  };

  const handleDelete = async () => {
    if (isPaymentBypassTester(selectedPractitioner)) {
      toast.info('The Payment Bypass Tester account is protected and cannot be deleted.');
      return;
    }

    if (
      !window.confirm(
        `Delete ${selectedPractitioner.firstName} ${selectedPractitioner.lastName}? This cannot be undone.`
      )
    )
      return;

    try {
      await axios.delete(
        `${API_URL}/admin/practitioners/${selectedPractitioner.id}`
      );
      toast.success('Practitioner deleted');
      setSelectedPractitioner(null);
      loadPractitioners();
    } catch (err) {
      toast.error(
        'Failed to delete: ' + (err.response?.data?.error || err.message)
      );
    }
  };

  const viewDetails = async (id) => {
    try {
      const response = await axios.get(
        `${API_URL}/admin/practitioners/${id}`
      );
      setSelectedPractitioner(response.data);
    } catch {
      toast.error('Failed to load practitioner details');
    }
  };

  const closeModal = () => {
    setSelectedPractitioner(null);
    setShowRejectModal(false);
    setRejectReason('');
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <Container>
      <Header>
        <h1>Practitioners</h1>
      </Header>

      {/* ── Filters ── */}
      <FiltersBar>
        <Select
          value={statusFilter}
          onChange={(e) =>
            setSearchParams({ status: e.target.value, search: searchQuery })
          }
        >
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </Select>

        <Input
          type="text"
          placeholder="Search by name or email…"
          value={searchQuery}
          onChange={(e) =>
            setSearchParams({ status: statusFilter, search: e.target.value })
          }
        />
      </FiltersBar>

      {/* ── Practitioners grid ── */}
      <PractitionersGrid>
        {loading
          ? Array.from({ length: 8 }).map((_, i) => (
              <SkeletonCard key={i}>
                <div className="shimmer" />
              </SkeletonCard>
            ))
          : practitioners.map((p) => (
              <PractitionerCard key={p.id}>
                <PractitionerImageWrapper>
                  {hasFileReference(p.profileImagePath) ? (
                    <PractitionerImage
                      src={getSecureFileUrl(p.profileImagePath)}
                      alt={`${p.firstName} ${p.lastName}`}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        if (e.target.nextSibling)
                          e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <NoImagePlaceholderCard
                    style={{
                      display: hasFileReference(p.profileImagePath)
                        ? 'none'
                        : 'flex',
                    }}
                  >
                    👤
                  </NoImagePlaceholderCard>
                  <StatusBadgeCard>
                    <Badge type={p.approvalStatus}>{p.approvalStatus}</Badge>
                  </StatusBadgeCard>
                </PractitionerImageWrapper>

                <PractitionerInfo>
                  <PractitionerName>
                    {p.firstName} {p.lastName}
                  </PractitionerName>
                  <PractitionerEmail>{p.email}</PractitionerEmail>
                  <PractitionerLicense>{getLicenseTypeDisplay(p)}</PractitionerLicense>

                  <PractitionerActions>
                    <ActionButton view onClick={() => viewDetails(p.id)}>
                      View Details
                    </ActionButton>
                    {p.approvalStatus === 'pending' && (
                      <>
                        <ActionButton
                          approve
                          onClick={() => handleApprove(p.id)}
                        >
                          ✓
                        </ActionButton>
                        <ActionButton
                          reject
                          onClick={() => {
                            setSelectedPractitioner(p);
                            setShowRejectModal(true);
                          }}
                        >
                          ✕
                        </ActionButton>
                      </>
                    )}
                  </PractitionerActions>
                </PractitionerInfo>
              </PractitionerCard>
            ))}
      </PractitionersGrid>

      {!loading && practitioners.length === 0 && (
        <PageStatus>No practitioners found for the selected filters.</PageStatus>
      )}

      {!loading && totalPages > 1 && (
        <PaginationBar aria-label="Practitioners pagination">
          <PageButton
            type="button"
            onClick={() => setPage((current) => Math.max(1, current - 1))}
            disabled={page <= 1}
          >
            Previous
          </PageButton>
          <PageStatus>
            Page {page} of {totalPages} • {totalPractitioners} practitioner{totalPractitioners === 1 ? '' : 's'}
          </PageStatus>
          <PageButton
            type="button"
            onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
            disabled={page >= totalPages}
          >
            Next
          </PageButton>
        </PaginationBar>
      )}

      {/* ── Detail modal ── */}
      {selectedPractitioner && !showRejectModal && (
        <ModalOverlay onClick={closeModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h2>
                {selectedPractitioner.firstName} {selectedPractitioner.lastName}
              </h2>
              <div className="header-actions">
                <Badge
                  type={selectedPractitioner.approvalStatus}
                  style={{ marginRight: 4 }}
                >
                  {selectedPractitioner.approvalStatus}
                </Badge>
                <button
                  className="icon-btn"
                  onClick={() => viewDetails(selectedPractitioner.id)}
                  title="Refresh"
                >
                  🔄
                </button>
                <button className="icon-btn" onClick={closeModal} title="Close">
                  ✕
                </button>
              </div>
            </ModalHeader>

            <ModalBody>
              {/* Media row */}
              <MediaGrid>
                {/* Profile photo */}
                <MediaSection>
                  <MediaLabel>Profile Photo</MediaLabel>
                  {selectedPractitioner.profileImagePath ? (
                    <>
                      <PreviewImage
                        src={getSecureFileUrl(
                          selectedPractitioner.profileImagePath
                        )}
                        alt="Profile"
                        onClick={() =>
                          openImage(
                            selectedPractitioner.profileImagePath,
                            'Profile Photo'
                          )
                        }
                        onError={(e) => {
                          e.target.style.display = 'none';
                          if (e.target.nextSibling)
                            e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <EmptyMedia style={{ display: 'none' }}>
                        <span className="icon">👤</span>
                        <span className="label">Image unavailable</span>
                      </EmptyMedia>
                    </>
                  ) : (
                    <EmptyMedia>
                      <span className="icon">👤</span>
                      <span className="label">No photo uploaded</span>
                    </EmptyMedia>
                  )}
                </MediaSection>

                {/* License document */}
                <MediaSection>
                  <MediaLabel>License Document</MediaLabel>
                  {selectedPractitioner.licenseDocumentPath ? (
                    <DocCard>
                      <div className="doc-icon">
                        {isMimePdf(selectedPractitioner.licenseDocumentPath)
                          ? '📋'
                          : '🖼️'}
                      </div>
                      <div className="doc-title">License Document</div>
                      <div className="doc-subtitle">
                        {isMimePdf(selectedPractitioner.licenseDocumentPath)
                          ? 'PDF — click to view in secure viewer'
                          : 'Image — click to view full size'}
                      </div>
                      <DocActions>
                        <DocBtn
                          primary
                          onClick={() =>
                            openFile(
                              selectedPractitioner.licenseDocumentPath,
                              'License Document'
                            )
                          }
                          disabled={loadingFile}
                        >
                          {loadingFile ? '⏳ Loading…' : '👁 View Document'}
                        </DocBtn>
                        <DocBtn
                          secondary
                          onClick={() =>
                            downloadFile(
                              selectedPractitioner.licenseDocumentPath,
                              `license-${selectedPractitioner.id}`
                            )
                          }
                        >
                          ⬇ Download
                        </DocBtn>
                      </DocActions>
                    </DocCard>
                  ) : (
                    <EmptyMedia>
                      <span className="icon">📄</span>
                      <span className="label">No document uploaded</span>
                    </EmptyMedia>
                  )}
                </MediaSection>
              </MediaGrid>

              {/* CV */}
              {selectedPractitioner.cvPath && (
                <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
                  <DocBtn
                    primary
                    onClick={() =>
                      openFile(selectedPractitioner.cvPath, 'CV')
                    }
                    disabled={loadingFile}
                  >
                    📄 View CV
                  </DocBtn>
                  <DocBtn
                    secondary
                    onClick={() =>
                      downloadFile(
                        selectedPractitioner.cvPath,
                        `cv-${selectedPractitioner.id}`
                      )
                    }
                  >
                    ⬇ Download CV
                  </DocBtn>
                </div>
              )}

              {/* Info grid */}
              <InfoGrid>
                {[
                  ['Full Name',    `${selectedPractitioner.firstName} ${selectedPractitioner.lastName}`],
                  ['Email',        selectedPractitioner.email],
                  ['Phone',        selectedPractitioner.phone || 'N/A'],
                  ['License Type', getLicenseTypeDisplay(selectedPractitioner)],
                  ['License Number', selectedPractitioner.licenseNumber],
                  ['License State',  selectedPractitioner.licenseState || 'N/A'],
                  ['License Expiry', selectedPractitioner.licenseExpiry
                    ? new Date(selectedPractitioner.licenseExpiry).toLocaleDateString()
                    : 'N/A'],
                  ['Years of Experience', selectedPractitioner.yearsOfExperience || 'N/A'],
                  ['Workplace',    selectedPractitioner.workplace || 'N/A'],
                  ['Registered On', new Date(selectedPractitioner.createdAt).toLocaleString()],
                ].map(([label, value]) => (
                  <InfoItem key={label}>
                    <label>{label}</label>
                    <span>{value}</span>
                  </InfoItem>
                ))}
              </InfoGrid>

              {/* Specializations */}
              {selectedPractitioner.specializations?.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <p
                    style={{
                      fontSize: '0.7rem',
                      color: '#94a3b8',
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      fontWeight: 700,
                      marginBottom: 8,
                    }}
                  >
                    Specializations
                  </p>
                  <SpecList>
                    {selectedPractitioner.specializations.map((s, i) => (
                      <span key={i}>{s}</span>
                    ))}
                  </SpecList>
                </div>
              )}

              {/* Bio */}
              {selectedPractitioner.bio && (
                <div style={{ marginBottom: 16 }}>
                  <p
                    style={{
                      fontSize: '0.7rem',
                      color: '#94a3b8',
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      fontWeight: 700,
                      marginBottom: 8,
                    }}
                  >
                    Bio
                  </p>
                  <p
                    style={{
                      color: '#334155',
                      lineHeight: 1.65,
                      fontSize: '0.9rem',
                    }}
                  >
                    {selectedPractitioner.bio}
                  </p>
                </div>
              )}

              {isPaymentBypassTester(selectedPractitioner) && (
                <ProtectedAccountNotice>
                  🔒 Payment Bypass Tester is a protected system/test account and cannot be deleted from the Admin Portal.
                </ProtectedAccountNotice>
              )}

              {/* Action buttons */}
              <ButtonGroup>
                <Button secondary onClick={closeModal}>
                  Close
                </Button>
                {selectedPractitioner.approvalStatus === 'pending' && (
                  <>
                    <Button danger onClick={() => setShowRejectModal(true)}>
                      Reject
                    </Button>
                    <Button
                      primary
                      onClick={() => handleApprove(selectedPractitioner.id)}
                    >
                      ✓ Approve
                    </Button>
                  </>
                )}
                <Button
                  danger
                  onClick={handleDelete}
                  disabled={isPaymentBypassTester(selectedPractitioner)}
                  title={
                    isPaymentBypassTester(selectedPractitioner)
                      ? 'Payment Bypass Tester is protected and cannot be deleted'
                      : 'Delete practitioner'
                  }
                  style={{ background: '#f97316' }}
                >
                  {isPaymentBypassTester(selectedPractitioner) ? '🔒 Protected' : '🗑 Delete'}
                </Button>
              </ButtonGroup>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* ── PDF Viewer (blob iframe — bypasses X-Frame-Options) ── */}
      {pdfViewer && (
        <PDFViewerOverlay>
          <PDFToolbar>
            <span className="title">📋 {pdfViewer.title}</span>
            <div className="toolbar-actions">
              <ToolbarBtn
                onClick={() => downloadFile(pdfViewer.fileRef, pdfViewer.title)}
              >
                ⬇ Download
              </ToolbarBtn>
              <ToolbarBtn onClick={() => setPdfViewer(null)}>
                ✕ Close
              </ToolbarBtn>
            </div>
          </PDFToolbar>
          <PDFFrame>
            <iframe src={pdfViewer.blobUrl} title={pdfViewer.title} />
          </PDFFrame>
        </PDFViewerOverlay>
      )}

      {/* ── Image lightbox ── */}
      {lightbox && (
        <LightboxOverlay onClick={() => setLightbox(null)}>
          <LightboxClose onClick={() => setLightbox(null)}>✕</LightboxClose>
          <LightboxImage
            src={lightbox.src}
            alt={lightbox.title}
            onClick={(e) => e.stopPropagation()}
          />
        </LightboxOverlay>
      )}

      {/* ── Global file-loading overlay ── */}
      {loadingFile && (
        <LightboxOverlay style={{ zIndex: 3000 }}>
          <SpinnerWrap>
            <Spinner />
            Loading file…
          </SpinnerWrap>
        </LightboxOverlay>
      )}

      {/* ── Reject modal ── */}
      {showRejectModal && (
        <ModalOverlay
          onClick={() => {
            setShowRejectModal(false);
            setRejectReason('');
          }}
        >
          <ModalContent
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 480 }}
          >
            <ModalHeader>
              <h2>Reject Application</h2>
              <div className="header-actions">
                <button
                  className="icon-btn"
                  onClick={() => {
                    setShowRejectModal(false);
                    setRejectReason('');
                  }}
                >
                  ✕
                </button>
              </div>
            </ModalHeader>
            <ModalBody>
              <p style={{ color: '#475569', marginBottom: 4 }}>
                You are about to reject the application for{' '}
                <strong>
                  {selectedPractitioner?.firstName}{' '}
                  {selectedPractitioner?.lastName}
                </strong>
                .
              </p>
              <TextArea
                placeholder="Reason for rejection (optional but recommended)…"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
              />
              <ButtonGroup>
                <Button
                  secondary
                  onClick={() => {
                    setShowRejectModal(false);
                    setRejectReason('');
                  }}
                >
                  Cancel
                </Button>
                <Button danger onClick={handleReject}>
                  Confirm Rejection
                </Button>
              </ButtonGroup>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default Practitioners;
